import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import { DepField, makeFiltersBuilder } from "@/features/FilterTable/utils";
import { preprocessTextAsyncAutocompleteField } from "@/features/FormCard/utils";
import { createCurrencyOption } from "@/hooks/useCurrencyOption";
import { countries_options } from "@/components/Form/CountryPhone/data";
import { z } from "zod";
import { useTranslation } from "react-i18next";

const filterL1PortfoliosSchema = z.object({
  vehicle_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  account_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  currency_contains: z.string().optional(),
  reporting_currency_contains: z.string().optional(),
  bank_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  bank_reporting_currency_contains: z.string().optional(),
  bank_country_contains: z.string().optional(),
  account_type_contains: z.string().optional(),
  code_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  collateral_contains: z.string().optional(),
});

export type FilterL1PortfoliosSchema = z.infer<typeof filterL1PortfoliosSchema>;

const portfolioDepFields: readonly DepField<FilterL1PortfoliosSchema>[] = [
  {
    name: "bank_name_contains",
    key: "bank_name",
    extract: "text",
    bucket: "equals",
  },
  {
    name: "vehicle_contains",
    key: "vehicle",
    extract: "text",
    bucket: "equals",
  },
  {
    name: "name_contains",
    key: "id",
    extract: "id",
    bucket: "equals",
  },
  {
    name: "code_contains",
    key: "code",
    extract: "text",
    bucket: "equals",
  },
  {
    name: "account_name_contains",
    key: "account_name",
    extract: "text",
    bucket: "equals",
  },
];

const TABLE_NAME = "l1_portfolios";

const client = "l1";

export function FilterL1Portfolios() {
  const { t, i18n } = useTranslation("tables");

  const fields: Field[] = [
    {
      name: "vehicle_contains",
      label: t(`${TABLE_NAME}.searchbox.vehicle_contains`, { defaultValue: "Vehicle / Beneficiary" }),
      type: "asyncAutocomplete",
      base_url: "l1/l1_portfolios",
      column_name: "vehicle",
      filters: makeFiltersBuilder<FilterL1PortfoliosSchema>(
        portfolioDepFields,
        "vehicle_contains",
      ),
    },
    {
      name: "account_name_contains",
      label: t(`${TABLE_NAME}.searchbox.account_name_contains`, { defaultValue: "Account group" }),
      type: "asyncAutocomplete",
      base_url: "l1/l1_portfolios",
      column_name: "account_name",
      filters: makeFiltersBuilder<FilterL1PortfoliosSchema>(
        portfolioDepFields,
        "account_name_contains",
      ),
    },
    {
      name: "name_contains",
      label: t(`${TABLE_NAME}.searchbox.name_contains`, { defaultValue: "Account" }),
      type: "asyncAutocomplete",
      base_url: "l1/l1_portfolios",
      column_name: "name",
      filters: makeFiltersBuilder<FilterL1PortfoliosSchema>(
        portfolioDepFields,
        "name_contains",
      ),
    },
    {
      name: "currency_contains",
      label: t(`${TABLE_NAME}.searchbox.currency_contains`, { defaultValue: "Portfolio currency" }),
      type: "autocomplete",
      options: createCurrencyOption(),
    },
    {
      name: "reporting_currency_contains",
      label: t(`${TABLE_NAME}.searchbox.reporting_currency_contains`, { defaultValue: "Reporting currency" }),
      type: "autocomplete",
      options: createCurrencyOption(),
    },
    {
      name: "bank_name_contains",
      label: t(`${TABLE_NAME}.searchbox.bank_name_contains`, { defaultValue: "Bank" }),
      type: "asyncAutocomplete",
      base_url: "l1/l1_portfolios",
      column_name: "bank_name",
      filters: makeFiltersBuilder<FilterL1PortfoliosSchema>(
        portfolioDepFields,
        "bank_name_contains",
      ),
    },
    {
      name: "account_type_contains",
      label: t(`${TABLE_NAME}.searchbox.account_type_contains`, { defaultValue: "Account type" }),
      type: "select",
      useTranslation: true,
      client: "l1",
      table_name: "l1_portfolios",
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      translate_key: "account_type",
    },
    {
      name: "bank_reporting_currency_contains",
      label: t(`${TABLE_NAME}.searchbox.bank_reporting_currency_contains`, { defaultValue: "Management currency" }),
      type: "autocomplete",
      options: createCurrencyOption(),
    },
    {
      name: "bank_country_contains",
      label: t(`${TABLE_NAME}.searchbox.bank_country_contains`, { defaultValue: "Bank country" }),
      type: "autocomplete",
      options: countries_options,
    },
    {
      name: "code_contains",
      label: t(`${TABLE_NAME}.searchbox.code_contains`, { defaultValue: "Code" }),
      type: "asyncAutocomplete",
      base_url: "l1/l1_portfolios",
      column_name: "code",
    },
    {
      name: "collateral_contains",
      label: t(`${TABLE_NAME}.searchbox.collateral_contains`, { defaultValue: "Pledged securities account" }),
      type: "select",
      useTranslation: false,
      options: [
        { value: "true", label: "Oui" },
        { value: "false", label: "Non" },
      ],
    },
  ];

  return (
    <FilterTable
      client={client}
      schema={filterL1PortfoliosSchema}
      tableName="l1_portfolios"
      fields={fields}
      columns={2}
    />
  );
}
